<script setup>
import { useAlert } from 'dashboard/composables';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import InboxSubtitle from './components/InboxSubtitle.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});

const inboxesList = computed(() => getters['inboxes/getInboxes'].value);
const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    const inbox = inboxesList.value.find(i => i.id === id);
    if (inbox && inbox.delete_requested_at) {
      useAlert(t('INBOX_MGMT.DELETE.API.MARKED_FOR_DELETION_MESSAGE'));
    } else {
      useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    }
  } catch (error) {
    // e.g. other inboxes still send their emails through this one
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE')
    );
  }
};

const restoreInbox = async inbox => {
  try {
    await store.dispatch('inboxes/restore', inbox.id);
    useAlert(t('INBOX_MGMT.DELETE.API.RESTORE_SUCCESS_MESSAGE'));
  } catch (error) {
    // e.g. it sends its emails through an inbox that is marked for deletion too
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        t('INBOX_MGMT.DELETE.API.RESTORE_ERROR_MESSAGE')
    );
  }
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto">
        <tbody
          class="divide-y divide-n-weak flex-1 text-slate-700 dark:text-slate-100"
        >
          <tr v-for="inbox in inboxesList" :key="inbox.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center flex-row gap-4">
                <Thumbnail
                  v-if="inbox.avatar_url"
                  class="bg-n-alpha-3 rounded-full p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                  :src="inbox.avatar_url"
                  :username="inbox.name"
                  size="48px"
                />
                <div
                  v-else
                  class="w-[48px] h-[48px] flex justify-center items-center bg-n-alpha-3 rounded-full p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <ChannelIcon class="size-5" :inbox="inbox" />
                </div>
                <div class="min-w-0">
                  <span
                    class="block font-medium"
                    :class="{
                      'text-red-600 dark:text-red-400':
                        inbox.delete_requested_at,
                    }"
                  >
                    <template v-if="inbox.internal_name">
                      {{ `${inbox.internal_name}  (${inbox.name})` }}
                    </template>
                    <template v-else>
                      {{ inbox.name }}
                    </template>
                    <i
                      v-if="inbox.delete_requested_at"
                      v-tooltip.top="
                        $t('INBOX_MGMT.DELETE.DELETE_REQUESTED_TOOLTIP')
                      "
                      class="i-lucide-info ml-1 inline-block text-slate-700 dark:text-slate-100"
                    />
                  </span>
                  <InboxSubtitle :inbox="inbox" />
                  <component
                    :is="isAdmin ? 'router-link' : 'span'"
                    v-if="inbox.reauthorization_required"
                    :to="
                      isAdmin
                        ? {
                            name: 'settings_inbox_show',
                            params: { inboxId: inbox.id },
                          }
                        : undefined
                    "
                    data-test-id="reconnect-required"
                    class="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-xs font-medium bg-n-ruby-3 text-n-ruby-11"
                  >
                    <i class="i-lucide-triangle-alert size-3.5" />
                    {{ $t('INBOX_MGMT.LIST.RECONNECT_REQUIRED') }}
                  </component>
                </div>
              </div>
            </td>

            <td class="py-4">
              <div class="flex gap-1 justify-end">
                <router-link
                  v-if="!inbox.delete_requested_at"
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                  />
                </router-link>
                <Button
                  v-if="isAdmin && !inbox.delete_requested_at"
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDelete(inbox)"
                />
                <Button
                  v-if="isAdmin && inbox.delete_requested_at"
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.RESTORE_BUTTON_TEXT')"
                  icon="i-lucide-undo-2"
                  xs
                  slate
                  faded
                  @click="restoreInbox(inbox)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
